import React from 'react';
import { findDOMNode } from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import styled from 'src/ui/library/styled';
import debounce from 'lodash.debounce';

const styles = (theme, props) => ({
  root: {
    border: `solid ${props.padding}px transparent`,
    position: 'relative',
    overflow: 'hidden',
    zIndex: '100',
  },
  active: {
    boxShadow: '0 0 7px 0 rgba(0,0,0,0.11)',
    border: `solid ${props.padding}px #FFF`,
    backgroundColor: '#FFF',
    visibility: 'visible',
    height: '100%',
    overflow: 'visible',
    '& $coverBoxshadow': {
      visibility: 'visible',
    },
    '& $content': {
      visibility: 'visible',
    },
  },
  trigger: {
    position: 'relative',
    cursor: 'pointer',
    border: 'none',
    padding: 0,
    background: 'none',
    /* account for the focus outline */
    height: 'calc(100% - 2px)',
    width: 'calc(100% - 2px)',
    left: '1px',
    top: '1px',
  },
  content: {
    position: 'absolute',
    overflow: 'visible',
    visibility: 'hidden',
    width: '100%',
    left: `-${props.padding}px`,
    padding: `16px ${props.padding}px 16px ${props.padding}px`,
    backgroundColor: '#FFF',
    boxShadow: '0 0 8px 0 rgba(0, 0, 0, 0.11)',
  },
  coverBoxshadow: {
    visibility: 'hidden',
    position: 'absolute',
    bottom: '-5px',
    width: `calc(100% + ${props.padding * 2}px)`,
    height: '6px',
    backgroundColor: '#FFF',
    zIndex: '2000',
    left: `-${props.padding}px`,
  },
  announceMessage: {
    clip: 'rect(0 0 0 0)',
    width: '1px',
    border: '0',
    height: '1px',
    margin: '-1px',
    padding: '0',
    overflow: 'hidden',
    position: 'absolute',
  },
});

class PopoutBase extends React.Component {
  static propTypes = {
    /** Orientation on where the Popout should open (left, right) */
    orientation: PropTypes.string,
    /** Container element - useful for pulling width/height from */
    container: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
    /** Allow hover to trigger open */
    hoverable: PropTypes.bool,
    /** Trigger for the Popout */
    trigger: PropTypes.node.isRequired,
    /** Content to display when Popout triggered */
    content: PropTypes.node.isRequired,
    /** Default padding to be applied on Popout */
    padding: PropTypes.number,
    /** Use width from parent container for Popout content width */
    useContainerWidth: PropTypes.bool,
    /** Reference callback to toggleActive() to trigger manual close of Popout */
    triggerClose: PropTypes.func,
    /** Reference callback to toggleActive() to trigger manual close of Popout */
    triggerOpen: PropTypes.func,
    /** Callback fired when Popout is opened */
    onOpen: PropTypes.func,
    /** Callback fired when Popout is closed */
    onClosed: PropTypes.func,
  };

  static defaultProps = {
    styleType: 'default',
    orientation: 'left',
    styles: {},
    autoResize: true,
    hoverable: true,
    padding: 8,
    open: false,
    buttonName: '',
  };

  state = {
    active: this.props.open,
    triggerWidth: 'auto',
    contentWidth: 'auto',
    contentHeight: 'auto',
    triggerHeight: 'auto',
    announceText: '',
  };

  componentDidMount() {
    this.ignoreModals = true;

    this.setDimensions();
    setTimeout(() => this.setDimensions(), 100); /* work around for now to retrieve true width of child */

    // there's a bug today where the width's sometimes are not correct when pulling the contentWidth for the provided Popout content
    // best work around for now is to disable autoResize if needed
    if (this.props.autoResize) {
      this._handleResize = debounce(this.handleResize, 250);
      window.addEventListener('resize', this._handleResize, false);
    }

    this.observer = new MutationObserver(this.mutationObserverCallback);
    this.observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['style'],
    });

    document.addEventListener('mousedown', this.handleClickOutside, false);
  }

  componentWillUnmount() {
    if (this.props.autoResize) {
      window.removeEventListener('resize', this._handleResize, false);
    }
    this.observer.disconnect();

    document.addEventListener('mousedown', this.handleClickOutside, false);
  }

  handleClickOutside = ({ target }) => {
    if (target.parentNode === null || target === null || target.className === 'Select-menu') {
      return;
    }

    //For mulstiselect dropdown option list
    if (target.nodeName === 'svg' && target.closest('.multiselect-option')) {
      return;
    }

    // the Dropdown components pop the option list via a portal which comes outside of
    // the Popout container which would then cause the popout to hide.
    // this is sort of a crude way to fix the issue by looking at the classes. probably won't scale, but solves for now.
    const selectClasses = [
      'Select-menu',
      'popout-include',
      'dropdown-control__option',
      'dropdown-control__menu',
      'Select-option',
      'option-text',
      'autocomplete-item-name',
      'autocomplete-item-wrapper',
    ];
    const classCheck = el => selectClasses.some(c => el.classList.contains(c) || el.classList.length === 0);

    if (this.popoutRef && !this.popoutRef.contains(target) && !classCheck(target)) {
      this.toggleActive(false);
    }
  };

  mutationObserverCallback = mutations => {
    if (mutations[0].target.style.overflow === 'hidden') this.ignoreModals = false;
    else this.ignoreModals = true;
  };

  handleResize = () => {
    if (window.innerWidth !== this.windowWidth) {
      this.windowWidth = window.innerWidth;
      this.setDimensions();
    }
  };

  setDimensions = () => {
    const { container } = this.props;

    const triggerEl = findDOMNode(this.triggerRef);
    const contentEl = findDOMNode(this.contentRef);
    let containerEl = null;

    if (triggerEl === null || contentEl === null) return;

    if (container) {
      const { current } = container;
      const targetEl = current === undefined ? container : current;
      containerEl = findDOMNode(targetEl);
    }

    const paddingSize = this.props.padding * 2;
    const newDimensions = {
      triggerHeight: triggerEl.offsetHeight + paddingSize,
      triggerWidth: triggerEl.offsetWidth + paddingSize,
      contentWidth: contentEl.offsetWidth,
      contentTop: triggerEl.offsetHeight + 4,
      parentWidth: (containerEl && containerEl.offsetWidth) || null,
      parentHeight: (containerEl && containerEl.offsetHeight) || null,
    };
    this.setState(newDimensions);
  };

  getTriggerStyles = () => {};

  getContentStyles = () => {
    const { contentWidth, contentTop, parentWidth, triggerWidth } = this.state;

    if (contentWidth === 'auto') return {};

    let finalWidth = triggerWidth > contentWidth ? triggerWidth : this.state.contentWidth;
    let minWidth = contentWidth > triggerWidth ? contentWidth : finalWidth * 2;

    if (finalWidth > window.outerHeight) finalWidth = window.outerHeight;

    if (this.props.useContainerWidth) {
      finalWidth = parentWidth;
      minWidth = 'auto';
    }

    let finalLeft = `-${this.props.padding}px`;

    if (this.props.orientation === 'left') {
      if (triggerWidth > finalWidth) {
        finalLeft = -this.state.triggerWidth - this.props.padding;
      } else {
        finalLeft = this.state.triggerWidth - finalWidth - this.props.padding;
        if (!this.props.useContainerWidth) {
          finalLeft -= this.props.padding * 2;
        }
      }

      if (!this.props.useContainerWidth) {
        finalWidth += this.props.padding * 2;
      }
    }

    return {
      top: contentTop,
      width: finalWidth,
      minWidth: minWidth,
      left: finalLeft,
    };
  };

  handleCallbacks = () => {
    const { active } = this.state;
    if (this.props.onOpen && active === true) this.props.onOpen();
    if (this.props.onClose && active === false) this.props.onClose();
  };

  toggleActive = status => {
    this.setState(
      prevState => ({ active: status !== undefined ? status : !prevState.active, announceText: this.props.buttonName + ' collapsed' }),
      () => this.handleCallbacks(),
    );
  };

  handleKeyDown = event => {
    if (event.keyCode === 27) {
      this.setState({ active: false });
    }
  };

  handleMouseEnter = status => {
    if (this.props.hoverable) {
      this.setState({ active: true }, () => this.handleCallbacks());
    }
  };

  handleMouseLeave = () => {
    if (this.props.hoverable && this.ignoreModals) {
      this.setState({ active: false }, () => this.handleCallbacks());
    }
  };

  handleClick = () => {
    this.setState({
      active: !this.state.active,
      announceText: this.state.active ? this.props.buttonName + ' collapsed' : this.props.buttonName + ' expanded',
    });
  };

  handleBlur = () => {
    this.setState({ active: false });
  };

  render() {
    const { classes, trigger, content } = this.props;
    const { active, triggerWidth } = this.state;

    const popoutClass = classNames({
      [classes.root]: true,
      [classes.active]: active,
      'popout-active': active,
    });

    if (this.props.triggerClose) {
      this.props.triggerClose(this.toggleActive.bind(null, false));
    }

    if (this.props.triggerOpen) {
      this.props.triggerOpen(this.toggleActive.bind(null, true));
    }

    return (
      <div
        className={classNames(popoutClass, 'popout-root')}
        ref={ref => (this.popoutRef = ref)}
        style={{ width: triggerWidth, height: '100%' }}
        onMouseLeave={this.handleMouseLeave}
        onMouseEnter={this.handleMouseEnter}
        onKeyDown={this.handleKeyDown}>
        <button
          className={classes.trigger}
          styles={this.getTriggerStyles()}
          tabIndex={0}
          onClick={this.handleClick}
          aria-label={this.props.buttonName || 'Click to Open'}
          aria-expanded={active}>
          {React.cloneElement(trigger, { ref: el => (this.triggerRef = el) })}
          <div className={classes.coverBoxshadow} />
        </button>
        <div className={classes.announceMessage} aria-live="polite" aria-atomic="true">
          {this.state.announceText}
        </div>
        <div className={`popup-main-container ${classes.content}`} style={this.getContentStyles()}>
          {React.cloneElement(content, { ref: el => (this.contentRef = el) })}
        </div>
      </div>
    );
  }
}

export { PopoutBase };
export default styled(PopoutBase)(styles);
